import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Station, Item, Category, SupplyProgram } from '@shared/types';
import { PROGRAM_LABELS } from '@shared/types';
import type { ItemsResponse, CategoriesResponse, RequestSubmitResponse } from '@shared/api-responses';
import { isValidEmail } from '@shared/validation';
import { apiFetch } from '../hooks/useApi';
import NumericInput from '../components/NumericInput';

interface Props {
  station: Station;
  program: SupplyProgram;
  onChangeStation: () => void;
  onChangeProgram: () => void;
  /**
   * The signed-in user, if any. Without one the form asks for a name + email —
   * crew reach it by a plain link and never sign in.
   */
  signedInAs?: { name: string; email: string } | null;
}

const SUBMITTER_KEY = 'supply-submitter';

/** Name + email remembered per device, like program and station. */
function loadSubmitter(): { name: string; email: string } {
  try {
    const saved = JSON.parse(localStorage.getItem(SUBMITTER_KEY) ?? 'null') as { name?: unknown; email?: unknown };
    return {
      name: typeof saved?.name === 'string' ? saved.name : '',
      email: typeof saved?.email === 'string' ? saved.email : '',
    };
  } catch {
    return { name: '', email: '' };
  }
}

function saveSubmitter(name: string, email: string) {
  try {
    localStorage.setItem(SUBMITTER_KEY, JSON.stringify({ name, email }));
  } catch {
    // Private mode / full storage: they'll just type it again next time.
  }
}

/**
 * Station staff pick quantities for what they need. Submitting turns the
 * request straight into its own order(s). No pars — purely "what we need."
 *
 * Scoped to one program's catalog: someone restocking the kitchen shouldn't
 * scroll past bleach to find peanut butter.
 */
export default function RequestForm({ station, program, onChangeStation, onChangeProgram, signedInAs }: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [qty, setQty] = useState<Record<number, number | null>>({});
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(() => loadSubmitter().name);
  const [email, setEmail] = useState(() => loadSubmitter().email);
  const [error, setError] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const anonymous = !signedInAs;

  useEffect(() => {
    setLoading(true);
    // Clear any in-progress quantities: they refer to the other program's items.
    setQty({});
    const q = `?program=${program}`;
    Promise.all([apiFetch<ItemsResponse>(`/items${q}`), apiFetch<CategoriesResponse>(`/categories${q}`)])
      .then(([i, c]) => {
        setItems(i.items.filter((it) => it.is_active));
        setCategories(c.categories);
      })
      .catch(() => setStatus('error'))
      .finally(() => setLoading(false));
  }, [program]);

  const catName = (id: number | null) => categories.find((c) => c.id === id)?.name ?? 'Uncategorized';

  async function submit() {
    const chosen = items
      .filter((it) => (qty[it.id] ?? 0) > 0)
      .map((it) => ({ itemId: it.id, itemName: it.name, qty: qty[it.id] as number }));
    if (chosen.length === 0) return;

    // Name + email are required without a session. Checked here so the person
    // is taken to the empty field rather than told off by the server.
    if (anonymous) {
      if (!name.trim()) {
        setStatus('error');
        setError('Enter your name.');
        nameRef.current?.focus();
        return;
      }
      if (!isValidEmail(email)) {
        setStatus('error');
        setError('Enter a valid email address.');
        emailRef.current?.focus();
        return;
      }
      saveSubmitter(name.trim(), email.trim());
    }

    setStatus('saving');
    setError(null);
    try {
      // Direct fetch (not apiFetch) so the worker's { error } message can be
      // shown verbatim — a wrong email domain or a rate limit is actionable.
      const res = await fetch('/api/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        // `program` is recorded as a hint; the worker still derives each line's
        // program from its catalog item.
        body: JSON.stringify({
          stationId: station.id,
          program,
          note: note || null,
          items: chosen,
          ...(anonymous ? { name: name.trim(), email: email.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(data?.error ?? null);
        setStatus('error');
        return;
      }
      const data = (await res.json().catch(() => null)) as RequestSubmitResponse | null;
      setSubmittedId(data?.requestId ?? null);
      setStatus('done');
      setQty({});
      setNote('');
    } catch {
      setStatus('error');
    }
  }

  // Group items by category for display.
  const grouped = categories
    .map((c) => ({ category: c, items: items.filter((it) => it.category_id === c.id) }))
    .filter((g) => g.items.length > 0);
  const uncategorized = items.filter((it) => !categories.some((c) => c.id === it.category_id));

  if (loading) return <div className="p-6 text-zinc-400">Loading items…</div>;

  // Cleaning sits here until its catalog is seeded from Podio. Without this the
  // screen is a bare form with a permanently disabled button and no explanation.
  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 text-center">
        <h1 className="text-xl font-bold text-white mb-2">No {PROGRAM_LABELS[program].toLowerCase()} items yet</h1>
        <p className="text-sm text-zinc-400 mb-6">
          This catalog hasn’t been set up. Ask logistics to add items, or pick a different program.
        </p>
        <button
          onClick={onChangeProgram}
          className="rounded-lg bg-dcvfd-accent px-4 py-2 text-sm font-semibold text-white"
        >
          Change program
        </button>
      </div>
    );
  }

  const selectedCount = items.filter((it) => (qty[it.id] ?? 0) > 0).length;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 pb-28">
      <h1 className="text-xl font-bold text-white mb-2">New request</h1>

      {/*
        Program and station are remembered per device, so they arrive already
        chosen. That's the fewest taps, but it must never be invisible -- this bar
        states both and switches either in one tap.
      */}
      <div className="glass mb-4 flex flex-wrap items-center gap-2 rounded-lg px-3 py-2">
        <button
          onClick={onChangeProgram}
          aria-label={`Change program (currently ${PROGRAM_LABELS[program]})`}
          className="rounded-md bg-surface-raised border border-border-subtle px-2.5 py-1 text-sm text-white hover:border-dcvfd-accent"
        >
          {PROGRAM_LABELS[program]}{' '}
          <span aria-hidden="true" className="text-zinc-500">
            ▾
          </span>
        </button>
        <span aria-hidden="true" className="text-zinc-600">
          ·
        </span>
        <button
          onClick={onChangeStation}
          aria-label={`Change station (currently ${station.name})`}
          className="rounded-md bg-surface-raised border border-border-subtle px-2.5 py-1 text-sm text-white hover:border-dcvfd-accent"
        >
          {station.name}{' '}
          <span aria-hidden="true" className="text-zinc-500">
            ▾
          </span>
        </button>
      </div>

      {anonymous ? (
        <div className="glass mb-4 space-y-2 rounded-lg px-3 py-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs text-zinc-400">
                Your name <span className="text-ems-red">*</span>
              </span>
              <input
                ref={nameRef}
                type="text"
                autoComplete="name"
                required
                aria-required="true"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-lg bg-surface-raised border border-border-subtle px-3 py-2 text-sm text-white"
              />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">
                Your email <span className="text-ems-red">*</span>
              </span>
              <input
                ref={emailRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                required
                aria-required="true"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@dcvfd.org"
                className="mt-1 w-full rounded-lg bg-surface-raised border border-border-subtle px-3 py-2 text-sm text-white"
              />
            </label>
          </div>
          <p className="text-xs text-zinc-500">
            So logistics knows who asked. Use your @dcvfd.org or @pwcgov.org email.
          </p>
        </div>
      ) : (
        <p className="mb-4 text-xs text-zinc-400">
          Submitting as <span className="text-white">{signedInAs.name}</span>
        </p>
      )}

      {status === 'done' && (
        <div className="mb-4 rounded-lg bg-ems-green/15 border border-ems-green/40 px-3 py-2 text-sm text-ems-green">
          {anonymous ? (
            <>Request{submittedId ? ` #${submittedId}` : ''} submitted. Thank you!</>
          ) : (
            <>
              Request submitted. Thank you! Follow it under{' '}
              <Link to="/my-requests" className="underline">
                My requests
              </Link>
              .
            </>
          )}
        </div>
      )}
      {status === 'error' && (
        <div
          role="alert"
          className="mb-4 rounded-lg bg-ems-red/15 border border-ems-red/40 px-3 py-2 text-sm text-ems-red"
        >
          {error ?? 'Something went wrong. Try again.'}
        </div>
      )}

      {[...grouped, ...(uncategorized.length ? [{ category: null, items: uncategorized }] : [])].map((g, idx) => (
        <section key={idx} className="mb-5">
          <h2 className="text-sm font-semibold text-zinc-300 mb-2">{g.category ? g.category.name : catName(null)}</h2>
          <div className="space-y-1">
            {g.items.map((it) => (
              <div key={it.id} className="glass flex items-center justify-between rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{it.name}</div>
                  <div className="text-xs text-zinc-500">
                    {it.unit ?? ''} {it.fulfillment_channel === 'vendor' ? '· vendor' : ''}
                  </div>
                </div>
                <NumericInput
                  value={qty[it.id] ?? null}
                  onChange={(v) => setQty((q) => ({ ...q, [it.id]: v }))}
                  min={0}
                  aria-label={`Quantity for ${it.name}`}
                />
              </div>
            ))}
          </div>
        </section>
      ))}

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Notes for logistics (optional)"
        className="w-full rounded-lg bg-surface-raised border border-border-subtle px-3 py-2 text-sm text-white"
        rows={2}
      />

      {/* Signed in, the page has the bottom nav (mobile) and sidebar (desktop) to clear; the public page has neither. */}
      <div
        className={`fixed left-0 right-0 bg-surface/95 backdrop-blur border-t border-border-subtle px-4 py-3 ${
          anonymous ? 'bottom-0' : 'bottom-16 md:bottom-0 md:ml-64'
        }`}
      >
        <button
          onClick={submit}
          disabled={selectedCount === 0 || status === 'saving'}
          className="mx-auto block w-full max-w-2xl rounded-lg bg-dcvfd-accent py-3 font-semibold text-white disabled:opacity-40"
        >
          {status === 'saving' ? 'Submitting…' : `Submit request (${selectedCount})`}
        </button>
      </div>
    </div>
  );
}
